<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { breakpointsBootstrapV5, useBreakpoints, useEventListener } from "@vueuse/core";
import { TuBasePopup, TuButton, TuInputText, ButtonSeverity, isNullish } from "@tinyurldev/tu-ui-vue";
import { FocusTrap } from "focus-trap-vue";

import useNotify from "@Composables/useNotify";
import { useTranslation } from "@Resources/js/composables";
import XOverlay from "@Modules/shared/store/XOverlay";
import { isValidEmail } from "@Helpers/functions/isValidEmail";
import { TeamInvitationRules } from "@Resources/js/TransformerObjects/TeamInvitationRules";
import type { Organization } from "@Modules/teams/Data/Models/OrganizationModel";
import InviteeChip from "@Modules/teams/components/InviteeChip.vue";
import { logError } from "@Resources/js/helpers/logger";
import type { TypedTeamInvitationList } from "@Resources/js/laravel";

//
// Setup
const overlay = XOverlay();
const { notifySuccess } = useNotify();
const { __ } = useTranslation();

const MAX_RECIPIENTS = TeamInvitationRules.max_invites;
const MAX_RECIPIENTS_MESSAGE = __("invite_user.max_recipients_error", { max: MAX_RECIPIENTS });

//
// Props
interface Props {
  organization: Organization;
  inviteUsers: (emails: string[]) => Promise<TypedTeamInvitationList | null>;
  existingEmails?: string[];
  notifyInvited?: () => void;
  errors?: Record<string, string>;
  clearErrors?: () => void;
}

const props = withDefaults(defineProps<Props>(), {
  existingEmails: () => [],
  notifyInvited: () => {},
  clearErrors: () => {},
});

//
// Data
const chips = ref<string[]>([]);
const email = ref("");
const errorMessage = ref("");
const busy = ref(false);

//
// Computed
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");
const atCapacity = computed(() => chips.value.length >= MAX_RECIPIENTS);
const canSend = computed(() => chips.value.length > 0 && !busy.value);

//
// Methods
function includesEmail(list: string[], email: string): boolean {
  const lower = email.toLowerCase();

  return list.some((entry) => entry.toLowerCase() === lower);
}

function commit(): void {
  const trimmedEmail = email.value.trim().toLowerCase();

  if (!trimmedEmail) {
    return;
  }

  if (atCapacity.value) {
    errorMessage.value = MAX_RECIPIENTS_MESSAGE;

    return;
  }

  if (!isValidEmail(trimmedEmail)) {
    errorMessage.value = __("invite_user.invalid_email_error", { email: trimmedEmail });

    return;
  }

  if (includesEmail(chips.value, trimmedEmail)) {
    errorMessage.value = __("invite_user.already_listed_error", { email: trimmedEmail });

    return;
  }

  if (includesEmail(props.existingEmails, trimmedEmail)) {
    errorMessage.value = __("invite_user.already_member_error", { email: trimmedEmail });

    return;
  }

  chips.value = [...chips.value, trimmedEmail];
  email.value = "";
  errorMessage.value = "";
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === "," || event.key === "Enter") {
    event.preventDefault();
    commit();
  }
}

function removeChip(index: number): void {
  chips.value = chips.value.filter((_, position) => position !== index);

  if (!atCapacity.value && errorMessage.value === MAX_RECIPIENTS_MESSAGE) {
    errorMessage.value = "";
  }
}

async function send(): Promise<void> {
  if (!canSend.value) {
    return;
  }

  busy.value = true;
  try {
    const result = await props.inviteUsers(chips.value);

    if (isNullish(result)) {
      return;
    }

    notifySuccess({ text: __("invite_user.invite_sent") });
    props.notifyInvited?.();
    close();
  } catch {
    logError("[InviteUserModal]: Invitations could not be sent!");
  } finally {
    busy.value = false;
  }

}

function close(): void {
  props.clearErrors();
  overlay.hide();
}

useEventListener(window, "keydown", (event: KeyboardEvent) => {
  if (event.key === "Escape") {
    close();
  }
});

//
// Watchers
watch(atCapacity, (isAtCapacity) => {
  if (isAtCapacity) {
    errorMessage.value = MAX_RECIPIENTS_MESSAGE;
  }
});

onMounted(() => {
  document.body.style.overflow = "hidden";
});

onBeforeUnmount(() => {
  document.body.style.overflow = "";
});

//
// Exposed Test Vars
defineExpose({ chips, email, errorMessage, busy, commit, send });
</script>

<template>
  <div
    class="v2-invite-user-modal-wrapper _invite_modal"
    :class="{ 'v2-invite-user-modal-wrapper--mobile': isMobileView }"
    @click.self="close"
  >
    <FocusTrap :escape-deactivates="false">
      <TuBasePopup
        id="v2-invite-user-modal"
        tabindex="-1"
        class="v2-invite-user-modal"
        :class="{ 'v2-invite-user-modal--mobile': isMobileView }"
        :is-closeable="!isMobileView"
        @close="close"
      >
        <template #title>
          <span class="_title">{{ __("invite_user.title") }}</span>
        </template>

        <template #content>
          <div class="v2-invite-user-modal__body">
            <p class="v2-invite-user-modal__lead">
              {{ __("invite_user.lead") }}
            </p>

            <div class="v2-invite-user-modal__field">
              <label
                class="v2-invite-user-modal__label"
                for="v2-invite-user-modal-email"
              >
                {{ __("invite_user.email_label") }}
              </label>

              <div
                v-if="chips.length"
                class="v2-invite-user-modal__chips"
              >
                <InviteeChip
                  v-for="(chip, index) in chips"
                  :key="chip"
                  :email="chip"
                  :invalid-reason="errors?.[chip]"
                  @remove="removeChip(index)"
                />
              </div>

              <div class="v2-invite-user-modal__input-row">
                <TuInputText
                  id="v2-invite-user-modal-email"
                  v-model="email"
                  class="v2-invite-user-modal__input _email_input"
                  :input="{ placeholder: __('invite_user.email_placeholder'), isDisabled: atCapacity }"
                  :copy="{ ...(errorMessage && { errorMessage }) }"
                  @keydown.native="onKeydown"
                />
                <TuButton
                  type="button"
                  class="v2-invite-user-modal__add _add"
                  aria-label="Add email"
                  label=""
                  @click="commit"
                >
                  <i
                    class="fa-solid fa-plus"
                    aria-hidden="true"
                  />
                </TuButton>
              </div>
            </div>
          </div>
        </template>

        <template #footer>
          <div class="v2-invite-user-modal__footer">
            <TuButton
              v-if="!isMobileView"
              class="_cancel"
              :label="__('invite_user.cancel_button')"
              is-outlined
              :severity="ButtonSeverity.CANCEL"
              :is-disabled="busy"
              @click="close"
            />
            <TuButton
              class="_send"
              :label="__('invite_user.send_button')"
              :is-block="isMobileView"
              :is-loading="busy"
              :is-disabled="!canSend"
              @click="send"
            />
          </div>
        </template>
      </TuBasePopup>
    </FocusTrap>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/position.scss" as position;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.v2-invite-user-modal-wrapper {
  position: fixed;
  inset: 0;
  z-index: position.$highest-z-index;

  display: flex;
  align-items: center;
  justify-content: center;
  padding: spacing.$xxs;

  background-color: color.$main-overlay;

  &--mobile {
    padding: 0;
  }
}

.v2-invite-user-modal {
  $self: &;

  max-width: 600px;

  &__body {
    display: flex;
    flex-flow: column;
    gap: spacing.$xxs;
  }

  &__lead {
    margin: 0;

    font-size: typography.$base-font-size;
    font-weight: typography.$font-weight-medium;
    line-height: typography.$base-line-height;
  }

  &__field {
    display: flex;
    flex-flow: column;
    gap: spacing.$xxxs;
  }

  &__label {
    margin: 0;

    font-size: typography.$base-font-size;
    font-weight: typography.$font-weight-semi-bold;
    line-height: typography.$base-line-height;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: spacing.$xxxs;
  }

  &__input-row {
    display: flex;
    gap: spacing.$xxxs;
    align-items: flex-start;
  }

  &__input {
    flex: 1 0 0;
    min-width: 0;
  }

  &__add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: spacing.$xxxs;

    font-size: typography.$base-font-size;
    color: color.$primary;

    background: transparent;
    border: 0;
    cursor: pointer;

    &:disabled {
      color: color.$text-light;
      cursor: not-allowed;
    }
  }

  &__footer {
    display: flex;
    gap: spacing.$xxs;
    align-items: end;
    justify-content: flex-end;
  }

  &--mobile {
    margin-top: auto;

    #{ $self }__chips {
      flex-flow: column;
      max-height: 250px;
      overflow-y: auto;
    }
  }
}
</style>
